// frontend/src/components/LiveTab.jsx
import GameCard from './GameCard';
import LiveBetSlip from './LiveBetSlip';
import { colors, fontFamily } from '../theme';
import { useDragScroll } from '../hooks';
import { splitLeagueName, groupLeaguesByPrefix, shouldGroupLeagues } from '../utils/homeTabHelpers';

export default function LiveTab({
  liveSports,
  activeLiveSport,
  onSelectLiveSport,
  liveOdds,
  liveBetSlip,
  showLiveBetSlip,
  onAddToLiveBetSlip,
  onUpdateLiveBetSlip,
  onRemoveLiveBet,
  onClearLiveBetSlip,
  onCloseLiveBetSlip,
  freePlayBalance,
  onBetPlaced,
  user,
  showToast,
  favoriteTeams,
  onToggleFavorite,
  onLoadProps,
  activeLeagueGroup,
  onSelectLeagueGroup,
  activeLeagueFilter,
  onSelectLeagueFilter,
}) {
  const sportTabScroll = useDragScroll();
  const leagueGroupScroll = useDragScroll();
  const leagueFilterScroll = useDragScroll();

  // Sports with many same-title leagues (esports by game, MMA/boxing by
  // promotion, ...) group by the "<Group> - <Rest>" prefix odds-api names
  // leagues with, same as the Home tab — an org that fields both a Dota 2
  // and a CS roster shouldn't have both teams' games mixed into one feed.
  const leagueList = Array.from(new Set(liveOdds.map(g => g.league || '').filter(Boolean))).sort();
  const leagueGroups = groupLeaguesByPrefix(leagueList);
  const hasLeagueGroups = shouldGroupLeagues(leagueGroups, leagueList.length);
  const groupNames = Array.from(leagueGroups.keys()).sort();
  const activeGroup = activeLeagueGroup || groupNames[0];
  const leaguesInActiveGroup = hasLeagueGroups ? (leagueGroups.get(activeGroup) || []) : leagueList;
  const activeFilter = activeLeagueFilter || (hasLeagueGroups ? null : leagueList[0]);

  const visibleOdds = leagueList.length <= 1
    ? liveOdds
    : hasLeagueGroups
      ? (activeFilter
          ? liveOdds.filter(g => (g.league || '') === activeFilter)
          : liveOdds.filter(g => splitLeagueName(g.league || '').group === activeGroup))
      : liveOdds.filter(g => (g.league || '') === (activeFilter || leagueList[0]));

  // liveBetSlip entries are { game, market, outcome, price, american } —
  // a different shape from the prematch betSlip's flat { game_id,
  // market_type, selection }, so GameCard takes this as an override rather
  // than assuming one fixed shape.
  const isInLiveBetSlip = (market, outcome, game) =>
    liveBetSlip.some(
      b => b.game.id === game.id && b.market.key === market.key && b.outcome.name === outcome.name
    );

  return (
    <div style={{ fontFamily }}>
      {liveSports.length > 0 && (
        <div
          ref={sportTabScroll.ref}
          className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto scrollbar-hide drag-scroll"
          onMouseDown={sportTabScroll.onMouseDown}
          onMouseLeave={sportTabScroll.onMouseLeave}
          onMouseUp={sportTabScroll.onMouseUp}
          onMouseMove={sportTabScroll.onMouseMove}
        >
          {liveSports.map(s => {
            const isActive = activeLiveSport === s.sport_key;
            return (
              <button
                key={s.sport_key}
                onClick={() => onSelectLiveSport(s.sport_key)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg transition text-xs md:text-sm"
                style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
              >
                <span className="text-base md:text-lg">{s.icon || '🎯'}</span>
                <span className="font-medium whitespace-nowrap">{s.sport_name}</span>
              </button>
            );
          })}
        </div>
      )}

      {activeLiveSport && hasLeagueGroups && (
        <div
          ref={leagueGroupScroll.ref}
          className="flex items-center gap-1.5 px-3 py-1.5 overflow-x-auto scrollbar-hide drag-scroll"
          onMouseDown={leagueGroupScroll.onMouseDown}
          onMouseLeave={leagueGroupScroll.onMouseLeave}
          onMouseUp={leagueGroupScroll.onMouseUp}
          onMouseMove={leagueGroupScroll.onMouseMove}
        >
          {groupNames.map(group => {
            const isActive = activeGroup === group;
            return (
              <button
                key={group}
                onClick={() => onSelectLeagueGroup(group)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg transition text-xs font-semibold"
                style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
              >
                <span>{group}</span>
              </button>
            );
          })}
        </div>
      )}

      {activeLiveSport && leaguesInActiveGroup.length > 1 && (
        <div
          ref={leagueFilterScroll.ref}
          className="flex items-center gap-1.5 px-3 py-1.5 overflow-x-auto scrollbar-hide drag-scroll"
          onMouseDown={leagueFilterScroll.onMouseDown}
          onMouseLeave={leagueFilterScroll.onMouseLeave}
          onMouseUp={leagueFilterScroll.onMouseUp}
          onMouseMove={leagueFilterScroll.onMouseMove}
        >
          {leaguesInActiveGroup.map(league => {
            const isActive = activeFilter === league;
            const label = hasLeagueGroups ? splitLeagueName(league).sub : league;
            return (
              <button
                key={league}
                onClick={() => onSelectLeagueFilter(league)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg transition text-xs"
                style={{ background: isActive ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
              >
                <span className="font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="px-3 pb-24 flex flex-col gap-1 mt-2">
        {liveSports.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: colors.textFaint }}>No live games right now</div>
        ) : !activeLiveSport ? (
          <div className="text-center py-12 text-sm" style={{ color: colors.textFaint }}>Select a sport above</div>
        ) : liveOdds.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: colors.textFaint }}>Waiting for live odds…</div>
        ) : (
          visibleOdds.map(game => (
            <GameCard
              key={game.id}
              game={game}
              onAddToBetSlip={onAddToLiveBetSlip}
              onLoadProps={onLoadProps}
              favoriteTeams={favoriteTeams}
              onToggleFavorite={onToggleFavorite}
              isInBetSlip={(market, outcome) => isInLiveBetSlip(market, outcome, game)}
            />
          ))
        )}
      </div>

      {showLiveBetSlip && liveBetSlip.length > 0 && (
        <div className="fixed bottom-20 right-4 w-96 lg:w-[28rem] z-[60]">
          <LiveBetSlip
            slip={liveBetSlip}
            onUpdateSlip={onUpdateLiveBetSlip}
            onRemove={onRemoveLiveBet}
            onClear={onClearLiveBetSlip}
            onClose={onCloseLiveBetSlip}
            currentOdds={liveOdds}
            freePlayBalance={freePlayBalance}
            onBetPlaced={onBetPlaced}
            user={user}
            showToast={showToast}
          />
        </div>
      )}
    </div>
  );
}
